import React from 'react';
import { MIN_DOB } from '../../utils/helpers';

export const DOB_MASK_PLACEHOLDER = 'MM/DD/YYYY';

// Use a non-leap reference year so we don't over-allow Feb 29 during typing;
// stricter calendar validation still happens in dobToIsoDate.
const daysInMonth = (month: number): number => {
  if (month < 1 || month > 12) return 31;
  return new Date(1999, month, 0).getDate();
};

// Digit-by-digit mask. Rules:
//  - Month: 01–12. First digit ≥2 auto-pads to 0X. Second digit that would
//    overflow (e.g. "1" then "5" = 15) is dropped.
//  - Day: 01–{daysInMonth}. Same auto-pad / drop-overflow logic.
//  - Year: up to 4 digits, no clamping here.
export const formatDobInput = (value: string): string => {
  const raw = value.replace(/\D/g, '').slice(0, 8);
  let mm = '';
  let dd = '';
  let yyyy = '';

  for (const ch of raw) {
    if (mm.length < 2) {
      if (mm.length === 0) {
        mm = parseInt(ch, 10) >= 2 ? `0${ch}` : ch;
      } else {
        const candidate = parseInt(`${mm}${ch}`, 10);
        if (candidate >= 1 && candidate <= 12) mm = `${mm}${ch}`;
      }
    } else if (dd.length < 2) {
      const max = daysInMonth(parseInt(mm, 10));
      const tenMax = Math.floor(max / 10);
      if (dd.length === 0) {
        dd = parseInt(ch, 10) > tenMax ? `0${ch}` : ch;
      } else {
        const candidate = parseInt(`${dd}${ch}`, 10);
        if (candidate >= 1 && candidate <= max) dd = `${dd}${ch}`;
      }
    } else if (yyyy.length < 4) {
      yyyy += ch;
    }
  }

  let result = mm;
  if (dd) result += `/${dd}`;
  if (yyyy) result += `/${yyyy}`;
  return result;
};

// Returns ISO YYYY-MM-DD, or null if the masked value isn't a complete, real date.
export const dobToIsoDate = (mmddyyyy: string): string | null => {
  const match = mmddyyyy.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const [, mm, dd, yyyy] = match;
  const month = parseInt(mm, 10);
  const day = parseInt(dd, 10);
  const year = parseInt(yyyy, 10);
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return `${yyyy}-${mm}-${dd}`;
};

// Inverse of dobToIsoDate: ISO YYYY-MM-DD -> MM/DD/YYYY display string.
export const isoDateToDob = (iso: string): string => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

export interface DobValidationOptions {
  /** ISO YYYY-MM-DD lower bound (inclusive). Defaults to MIN_DOB (1920-01-01). */
  minDate?: string;
  /** ISO YYYY-MM-DD upper bound (inclusive). */
  maxDate?: string;
}

export interface DobValidationResult {
  iso: string | null;
  error: string | null;
}

// Validates a masked MM/DD/YYYY value against format, calendar reality, and
// optional range. Returns { iso, error }: `iso` is non-null only when fully valid.
export const validateDob = (
  mmddyyyy: string,
  { minDate = MIN_DOB, maxDate }: DobValidationOptions = {}
): DobValidationResult => {
  const iso = dobToIsoDate(mmddyyyy);
  if (!iso) {
    return { iso: null, error: `Please enter a valid date in ${DOB_MASK_PLACEHOLDER} format.` };
  }
  if (iso < minDate) {
    return { iso: null, error: `Date of birth must be on or after ${isoDateToDob(minDate)}.` };
  }
  if (maxDate && iso > maxDate) {
    return { iso: null, error: `Date of birth must be on or before ${isoDateToDob(maxDate)}.` };
  }
  return { iso, error: null };
};

interface DobInputProps {
  /** Masked display value, e.g. "12/15/1990". */
  value: string;
  /** Called with the next masked display value. */
  onChange: (value: string) => void;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export const DobInput = ({
  value,
  onChange,
  id,
  required,
  disabled,
  className,
  placeholder = DOB_MASK_PLACEHOLDER,
}: DobInputProps) => {
  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="bday"
      placeholder={placeholder}
      maxLength={DOB_MASK_PLACEHOLDER.length}
      value={value}
      required={required}
      disabled={disabled}
      className={className}
      onChange={(e) => onChange(formatDobInput(e.target.value))}
    />
  );
};
